import { colors, glassCard, typography } from '../../theme';
import React, { useEffect, useState } from 'react';
import {
    collection, query, where, getDocs, Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
    BarChart, Bar, LineChart, Line, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown, ContentCut, People } from '@mui/icons-material';

// ─── colors ───────────────────────────────────────────────────────────────────
const GOLD   = colors.primary;
const GREEN  = colors.success;
const PURPLE = colors.info;
const RED    = colors.error;
const DARK   = colors.bgCard;
const BORDER = colors.border;

// ─── Revenue resolver ─────────────────────────────────────────────────────────
function getBookingRevenue(b) {
    if (b.totalPrice   != null) return b.totalPrice;
    if (b.servicePrice != null) return (b.servicePrice ?? 0) + (b.surcharge ?? 0);
    return b.price ?? 0;
}

// ─── Custom tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, prefix = 'RM ' }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ bgcolor: colors.bg, border: `1px solid ${BORDER}`, borderRadius: 2, px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: 11, color: colors.textMuted, mb: 0.5 }}>{label}</Typography>
            {payload.map((p, i) => (
                <Typography key={i} sx={{ fontSize: 12, color: p.color, fontWeight: 600 }}>
                    {p.name}: {prefix}{typeof p.value === 'number' ? p.value.toFixed(2) : p.value}
                </Typography>
            ))}
        </Box>
    );
};

// ─── Summary card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, sub, color = GOLD, trend, prefix = 'RM ' }) => (
    <Box sx={{ bgcolor: DARK, border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2, flex: 1, ...glassCard }}>
        <Typography sx={{ fontSize: 11, color: colors.textFaint, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontFamily: typography.serif, fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>
            {prefix}{typeof value === 'number' ? value.toFixed(2) : value}
        </Typography>
        {sub && <Typography sx={{ fontSize: 10, color: colors.textFaintest, mt: 0.3 }}>{sub}</Typography>}
        {trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.5 }}>
                {trend >= 0
                    ? <TrendingUp sx={{ fontSize: 12, color: GREEN }} />
                    : <TrendingDown sx={{ fontSize: 12, color: RED }} />}
                <Typography sx={{ fontSize: 10, color: trend >= 0 ? GREEN : RED }}>
                    {trend >= 0 ? '+' : ''}{trend.toFixed(1)}% vs last week
                </Typography>
            </Box>
        )}
    </Box>
);

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader = ({ title, sub }) => (
    <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontFamily: typography.serif, fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>{title}</Typography>
        {sub && <Typography sx={{ fontSize: 11, color: colors.textFaintest, mt: 0.2 }}>{sub}</Typography>}
    </Box>
);

// ─── Chart card ───────────────────────────────────────────────────────────────
const ChartCard = ({ children, title, sub }) => (
    <Box sx={{ bgcolor: DARK, border: `1px solid ${BORDER}`, borderRadius: 3, p: 2, ...glassCard }}>
        {title && <SectionHeader title={title} sub={sub} />}
        {children}
    </Box>
);

// ─── TODAY TAB ────────────────────────────────────────────────────────────────
const TodayTab = ({ bookings, barbers }) => {
    const today    = new Date(); today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today); todayEnd.setDate(todayEnd.getDate() + 1);

    const todayBookings = bookings.filter(b => {
        const d = b.scheduledAt?.toDate?.();
        return d >= today && d < todayEnd;
    });

    const sum = (arr) => arr.reduce((s, b) => s + getBookingRevenue(b), 0);

    const todayRevenue    = sum(todayBookings);
    const todaySurcharge  = todayBookings.reduce((s, b) => s + (b.surcharge ?? 0), 0);
    const todayCount      = todayBookings.length;
    const todayAvg        = todayCount > 0 ? todayRevenue / todayCount : 0;

    // ── Per-barber breakdown ───────────────────────────────────────────────
    const barberBreakdown = barbers.map(barber => {
        const bb = todayBookings.filter(b => b.barberId === barber.id);
        const rev = sum(bb);
        const surcharge = bb.reduce((s, b) => s + (b.surcharge ?? 0), 0);
        const services  = bb.reduce((s, b) => s + (b.servicePrice ?? b.price ?? 0), 0);
        return {
            id:       barber.id,
            name:     barber.name,
            image:    barber.image,
            count:    bb.length,
            revenue:  rev,
            surcharge,
            services,
        };
    }).filter(b => b.count > 0).sort((a, b) => b.revenue - a.revenue);

    // ── Hourly breakdown ───────────────────────────────────────────────────
    const hourlyData = Array.from({ length: 10 }, (_, i) => {
        const hour = 8 + i;
        const h    = hour % 12 === 0 ? 12 : hour % 12;
        const hB   = todayBookings.filter(b => Math.floor((b.slotMinutes ?? 0) / 60) === hour);
        return {
            time:    `${h}${hour < 12 ? 'AM' : 'PM'}`,
            revenue: sum(hB),
            count:   hB.length,
        };
    });

    const topHour = [...hourlyData].sort((a, b) => b.revenue - a.revenue)[0];

    if (todayCount === 0) return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontSize: 36, mb: 1 }}>📭</Typography>
            <Typography sx={{ fontFamily: typography.serif, fontSize: 16, fontWeight: 700, color: colors.textMuted }}>No completed bookings today</Typography>
            <Typography sx={{ fontSize: 12, color: colors.textFaintest, mt: 0.5 }}>Revenue will appear here as bookings are completed</Typography>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Summary row */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <SummaryCard label="Today's Revenue" value={todayRevenue} color={GOLD}   sub={`${todayCount} completed`} />
                <SummaryCard label="Avg per Booking" value={todayAvg}     color={PURPLE} sub="today" />
            </Box>

            {/* Surcharge pill */}
            {todaySurcharge > 0 && (
                <Box sx={{ bgcolor: colors.primaryDim, border: `1px solid ${GOLD}`, borderRadius: 2, px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 14 }}>⭐</Typography>
                        <Typography sx={{ fontSize: 12, color: colors.textPrimary }}>Barber selection fees today</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: typography.serif, fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                        RM {todaySurcharge.toFixed(2)}
                    </Typography>
                </Box>
            )}

            {/* Revenue by barber */}
            <ChartCard title="Revenue by Barber" sub="Today's earnings per barber">
                {barberBreakdown.map((b, i) => {
                    const pct   = todayRevenue > 0 ? (b.revenue / todayRevenue) * 100 : 0;
                    const isTop = i === 0;
                    return (
                        <Box key={b.id} sx={{ mb: i < barberBreakdown.length - 1 ? 2.5 : 0 }}>
                            {/* Barber header row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                {/* rank */}
                                <Typography sx={{ fontFamily: typography.serif, fontSize: 18, fontWeight: 900, color: isTop ? GOLD : colors.textFaintest, width: 20, textAlign: 'center', lineHeight: 1 }}>
                                    {i + 1}
                                </Typography>
                                {/* avatar */}
                                {b.image ? (
                                    <Box component="img" src={b.image} alt={b.name}
                                        sx={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${isTop ? GOLD : BORDER}`, flexShrink: 0 }} />
                                ) : (
                                    <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: colors.primaryDim, border: `2px solid ${isTop ? GOLD : BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: 16 }}>✂️</Typography>
                                    </Box>
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: isTop ? 700 : 500, color: isTop ? GOLD : colors.textPrimary }}>{b.name}</Typography>
                                        <Typography sx={{ fontFamily: typography.serif, fontSize: 16, fontWeight: 700, color: isTop ? GOLD : colors.textMuted }}>
                                            RM {b.revenue.toFixed(2)}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 11, color: colors.textFaint }}>{b.count} booking{b.count !== 1 ? 's' : ''}</Typography>
                                </Box>
                            </Box>

                            {/* Progress bar */}
                            <Box sx={{ bgcolor: colors.bg, borderRadius: 1, height: 5, overflow: 'hidden', mb: 0.8 }}>
                                <Box sx={{ height: '100%', borderRadius: 1, background: isTop ? `linear-gradient(90deg, ${GOLD}, ${GOLD}88)` : `linear-gradient(90deg, ${GREEN}, ${GREEN}66)`, width: `${pct}%`, transition: 'width .8s ease' }} />
                            </Box>

                            {/* Service vs surcharge breakdown */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {b.services > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, bgcolor: colors.bgDeep, border: `1px solid ${BORDER}`, borderRadius: 1 }}>
                                        <Typography sx={{ fontSize: 10 }}>✂️</Typography>
                                        <Typography sx={{ fontSize: 10, color: colors.textFaint }}>Services: <Typography component="span" sx={{ fontWeight: 600, color: colors.textMuted, fontSize: 10 }}>RM {b.services.toFixed(2)}</Typography></Typography>
                                    </Box>
                                )}
                                {b.surcharge > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, bgcolor: colors.primaryDim, border: `1px solid ${colors.primaryBorder}`, borderRadius: 1 }}>
                                        <Typography sx={{ fontSize: 10 }}>⭐</Typography>
                                        <Typography sx={{ fontSize: 10, color: colors.textFaint }}>Selection fee: <Typography component="span" sx={{ fontWeight: 600, color: GOLD, fontSize: 10 }}>RM {b.surcharge.toFixed(2)}</Typography></Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </ChartCard>

            {/* Hourly revenue chart */}
            <ChartCard title="Hourly Revenue" sub="Revenue earned per hour today">
                {topHour?.revenue > 0 && (
                    <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp sx={{ fontSize: 14, color: GREEN }} />
                        <Typography sx={{ fontSize: 11, color: GREEN }}>
                            Peak: <Typography component="span" sx={{ fontWeight: 700, fontSize: 11, color: GREEN }}>{topHour.time}</Typography> · RM {topHour.revenue.toFixed(2)}
                        </Typography>
                    </Box>
                )}
                <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={hourlyData} barCategoryGap="35%">
                        <defs>
                            <linearGradient id="hourGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={GOLD} stopOpacity={0.9} />
                                <stop offset="100%" stopColor={GOLD} stopOpacity={0.3} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                        <XAxis dataKey="time" tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `RM${v}`} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff08' }} />
                        <Bar dataKey="revenue" name="Revenue" fill="url(#hourGrad)" radius={[4,4,0,0]} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

        </Box>
    );
};

// ─── OVERVIEW TAB ─────────────────────────────────────────────────────────────
const OverviewTab = ({ bookings, barbers }) => {
    const [range, setRange] = useState('week');

    const today         = new Date(); today.setHours(0, 0, 0, 0);
    const todayEnd      = new Date(today); todayEnd.setDate(todayEnd.getDate() + 1);
    const weekStart     = new Date(today); weekStart.setDate(weekStart.getDate() - 6);
    const lastWeekStart = new Date(weekStart); lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastWeekEnd   = new Date(weekStart);

    const inRange = (b, from, to) => {
        const d = b.scheduledAt?.toDate?.();
        return d >= from && d < to;
    };

    const sum = (arr) => arr.reduce((s, b) => s + getBookingRevenue(b), 0);

    const todayBookings    = bookings.filter(b => inRange(b, today, todayEnd));
    const weekBookings     = bookings.filter(b => inRange(b, weekStart, todayEnd));
    const lastWeekBookings = bookings.filter(b => inRange(b, lastWeekStart, lastWeekEnd));

    const todayRevenue    = sum(todayBookings);
    const weekRevenue     = sum(weekBookings);
    const lastWeekRevenue = sum(lastWeekBookings);
    const totalRevenue    = sum(bookings);
    const totalCount      = bookings.length;
    const avgPerBooking   = totalCount > 0 ? totalRevenue / totalCount : 0;
    const surchargeTotal  = bookings.reduce((s, b) => s + (b.surcharge ?? 0), 0);

    const weekTrend = lastWeekRevenue > 0
        ? ((weekRevenue - lastWeekRevenue) / lastWeekRevenue) * 100
        : 0;

    const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

    const last7Days = Array.from({ length: 7 }, (_, i) => {
        const d    = new Date(today); d.setDate(d.getDate() - (6 - i));
        const dEnd = new Date(d);     dEnd.setDate(dEnd.getDate() + 1);
        const dayB = bookings.filter(b => inRange(b, d, dEnd));
        return { day: DAY_LABELS[d.getDay()], revenue: sum(dayB), count: dayB.length };
    });

    const last4Weeks = Array.from({ length: 4 }, (_, i) => {
        const wEnd   = new Date(today); wEnd.setDate(wEnd.getDate() - i * 7);
        const wStart = new Date(wEnd);  wStart.setDate(wStart.getDate() - 6);
        const wb     = bookings.filter(b => inRange(b, wStart, wEnd));
        return { week: `W${4 - i}`, revenue: sum(wb), count: wb.length };
    }).reverse();

    const chartData = range === 'week' ? last7Days : last4Weeks;
    const chartKey  = range === 'week' ? 'day' : 'week';

    const serviceMap = {};
    bookings.forEach(b => {
        const svc = b.service ?? 'Unknown';
        if (!serviceMap[svc]) serviceMap[svc] = { name: svc, count: 0, revenue: 0 };
        serviceMap[svc].count++;
        serviceMap[svc].revenue += getBookingRevenue(b);
    });
    const serviceData = Object.values(serviceMap).sort((a, b) => b.revenue - a.revenue);

    const barberData = barbers.map(barber => {
        const bb = bookings.filter(b => b.barberId === barber.id);
        return { name: barber.name.split(' ')[0], revenue: sum(bb), count: bb.length };
    }).filter(b => b.count > 0).sort((a, b) => b.revenue - a.revenue);

    const dayRevenue = Array(7).fill(0);
    bookings.forEach(b => {
        const d = b.scheduledAt?.toDate?.();
        if (d) dayRevenue[d.getDay()] += getBookingRevenue(b);
    });
    const topDay = DAY_LABELS[dayRevenue.indexOf(Math.max(...dayRevenue))];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <SummaryCard label="Today"     value={todayRevenue} color={GOLD}   sub={`${todayBookings.length} bookings`} />
                <SummaryCard label="This Week" value={weekRevenue}  color={PURPLE} sub={`${weekBookings.length} bookings`} trend={weekTrend} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <SummaryCard label="Total (90d)"     value={totalRevenue}  color={GREEN} sub="last 90 days" />
                <SummaryCard label="Avg per Booking" value={avgPerBooking} color={GOLD}  sub={`${totalCount} completed`} />
            </Box>

            {surchargeTotal > 0 && (
                <Box sx={{ bgcolor: colors.primaryDim, border: `1px solid ${GOLD}`, borderRadius: 2, px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 14 }}>⭐</Typography>
                        <Typography sx={{ fontSize: 12, color: colors.textPrimary }}>Barber selection fees collected</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: typography.serif, fontSize: 14, fontWeight: 700, color: GOLD }}>
                        RM {surchargeTotal.toFixed(2)}
                    </Typography>
                </Box>
            )}

            {topDay && totalRevenue > 0 && (
                <Box sx={{ bgcolor: colors.successBg, border: `1px solid ${colors.successBorder}`, borderRadius: 2, px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp sx={{ fontSize: 16, color: GREEN }} />
                    <Typography sx={{ fontSize: 12, color: GREEN }}>
                        Best day: <Typography component="span" sx={{ fontWeight: 700, fontSize: 12, color: GREEN }}>{topDay}</Typography> earns the most on average
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
                {[['week', 'Last 7 Days'], ['month', 'Last 4 Weeks']].map(([r, label]) => (
                    <Box key={r} onClick={() => setRange(r)} sx={{ px: 2, py: 0.6, borderRadius: 2, cursor: 'pointer', bgcolor: range === r ? GOLD : DARK, border: `1px solid ${range === r ? GOLD : BORDER}`, transition: 'all .2s' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: range === r ? colors.bg : colors.textMuted }}>{label}</Typography>
                    </Box>
                ))}
            </Box>

            <ChartCard title={range === 'week' ? 'Revenue — Last 7 Days' : 'Revenue — Last 4 Weeks'}>
                <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chartData} barCategoryGap="35%">
                        <defs>
                            <linearGradient id="revenueGrad2" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={GOLD} stopOpacity={0.9} />
                                <stop offset="100%" stopColor={GOLD} stopOpacity={0.4} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                        <XAxis dataKey={chartKey} tick={{ fill: colors.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `RM${v}`} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff08' }} />
                        <Bar dataKey="revenue" name="Revenue" fill="url(#revenueGrad2)" radius={[4,4,0,0]} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Completed Bookings" sub="Number of bookings completed per day/week">
                <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={chartData}>
                        <defs>
                            <linearGradient id="countGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={PURPLE} stopOpacity={0.3} />
                                <stop offset="100%" stopColor={PURPLE} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                        <XAxis dataKey={chartKey} tick={{ fill: colors.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip content={<CustomTooltip prefix="" />} cursor={{ stroke: PURPLE, strokeWidth: 1 }} />
                        <Area type="monotone" dataKey="count" name="Bookings" stroke={PURPLE} fill="url(#countGrad)" strokeWidth={2} dot={{ fill: PURPLE, r: 3 }} />
                    </AreaChart>
                </ResponsiveContainer>
            </ChartCard>

            {serviceData.length > 0 && (
                <ChartCard title="Revenue by Service">
                    {serviceData.map((svc, i) => {
                        const pct = totalRevenue > 0 ? (svc.revenue / totalRevenue) * 100 : 0;
                        return (
                            <Box key={svc.name} sx={{ mb: i < serviceData.length - 1 ? 2 : 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ContentCut sx={{ fontSize: 13, color: colors.textFaintest }} />
                                        <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>{svc.name}</Typography>
                                        <Box sx={{ px: 0.8, py: 0.1, bgcolor: colors.bg, border: `1px solid ${BORDER}`, borderRadius: 1 }}>
                                            <Typography sx={{ fontSize: 9, color: colors.textFaint }}>{svc.count}x</Typography>
                                        </Box>
                                    </Box>
                                    <Typography sx={{ fontFamily: typography.serif, fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>RM {svc.revenue.toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ bgcolor: colors.bg, borderRadius: 1, height: 5, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', borderRadius: 1, background: `linear-gradient(90deg, ${GOLD}, ${GOLD}88)`, width: `${pct}%`, transition: 'width .8s ease' }} />
                                </Box>
                                <Typography sx={{ fontSize: 10, color: colors.textFaintest, mt: 0.3 }}>{pct.toFixed(1)}% of total</Typography>
                            </Box>
                        );
                    })}
                </ChartCard>
            )}

            {barberData.length > 0 && (
                <ChartCard title="Revenue by Barber" sub="Last 90 days">
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={barberData} barCategoryGap="40%" layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} horizontal={false} />
                            <XAxis type="number" tick={{ fill: colors.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `RM${v}`} />
                            <YAxis type="category" dataKey="name" tick={{ fill: colors.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} width={50} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff08' }} />
                            <Bar dataKey="revenue" name="Revenue" fill={GREEN} radius={[0,4,4,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {barberData.map((b, i) => (
                            <Box key={b.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography sx={{ fontFamily: typography.serif, fontSize: 16, fontWeight: 700, color: i === 0 ? GOLD : colors.textFaintest, width: 20, textAlign: 'center' }}>
                                    {i + 1}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: 13, color: colors.textPrimary, fontWeight: i === 0 ? 700 : 400 }}>{b.name}</Typography>
                                        <Typography sx={{ fontSize: 13, color: i === 0 ? GOLD : colors.textMuted, fontWeight: 700 }}>RM {b.revenue.toFixed(2)}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 10, color: colors.textFaintest }}>{b.count} bookings</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </ChartCard>
            )}

            <ChartCard title="Best Days" sub="Average revenue by day of week">
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    {DAY_LABELS.map((day, i) => {
                        const maxRev = Math.max(...dayRevenue, 1);
                        const pct    = dayRevenue[i] / maxRev;
                        const isTop  = dayRevenue[i] === Math.max(...dayRevenue) && dayRevenue[i] > 0;
                        return (
                            <Box key={day} sx={{ flex: 1, textAlign: 'center' }}>
                                <Box sx={{ height: 60, borderRadius: 1.5, mb: 0.5, bgcolor: `rgba(0, 255, 0, ${Math.max(pct * 0.9, 0.08)})`, border: `1px solid ${isTop ? GOLD : BORDER}`, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', pb: 0.5 }}>
                                    {isTop && <Typography sx={{ fontSize: 10 }}>⭐</Typography>}
                                </Box>
                                <Typography sx={{ fontSize: 10, color: isTop ? GREEN : colors.textPrimary }}>{day}</Typography>
                                <Typography sx={{ fontSize: 9, color: colors.textPrimary }}>RM{dayRevenue[i].toFixed(0)}</Typography>
                            </Box>
                        );
                    })}
                </Box>
            </ChartCard>

            {totalRevenue === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ fontSize: 36, mb: 2 }}>💰</Typography>
                    <Typography sx={{ fontSize: 15, color: colors.textFaint }}>No revenue data yet</Typography>
                    <Typography sx={{ fontSize: 12, color: colors.textFaintest, mt: 0.5 }}>
                        Revenue is tracked from completed bookings with a price set
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

// ─── Main Revenue Component ───────────────────────────────────────────────────
const RevenueScreen = ({ shopId, barbers = [] }) => {
    const [loading,  setLoading]  = useState(true);
    const [bookings, setBookings] = useState([]);
    const [tab,      setTab]      = useState('today'); // 'today' | 'overview'

    useEffect(() => {
        if (!shopId) return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const from = new Date();
                from.setDate(from.getDate() - 90);
                from.setHours(0, 0, 0, 0);
                const q = query(
                    collection(db, 'bookings'),
                    where('shopId',      '==', shopId),
                    where('scheduledAt', '>=', Timestamp.fromDate(from)),
                    where('status',      '==', 'completed'),
                );
                const snap = await getDocs(q);
                setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        fetchData();
    }, [shopId]);

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: GOLD }} />
        </Box>
    );

    return (
        <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5, pb: 4 }}>

            <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>Revenue</Typography>

            {/* Tab switcher */}
            <Box sx={{ display: 'flex', gap: 1, bgcolor: colors.bgDeep, border: `1px solid ${BORDER}`, borderRadius: 2, p: 0.5 }}>
                {[['today', '📅 Today'], ['overview', '📊 Overview']].map(([key, label]) => (
                    <Box key={key} onClick={() => setTab(key)} sx={{ flex: 1, textAlign: 'center', py: 0.8, borderRadius: 1.5, cursor: 'pointer', bgcolor: tab === key ? GOLD : 'transparent', transition: 'all .2s' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tab === key ? '#322920' : colors.textMuted }}>{label}</Typography>
                    </Box>
                ))}
            </Box>

            {tab === 'today'
                ? <TodayTab    bookings={bookings} barbers={barbers} />
                : <OverviewTab bookings={bookings} barbers={barbers} />
            }
        </Box>
    );
};

export default RevenueScreen;